import {
  type CashbookAccess,
  type CashbookPermissionOverrides,
  hasCashbookPermissionOverrides,
  parseCashbookPermissionOverrides,
  resolveCashbookAccess
} from './cashbook-access.js';
import type { OrgRole } from './org.js';
import { canManageTeam, canManageUsers, canPostDocuments } from './org.js';

export const MODULE_IDS = [
  'dashboard',
  'cashbook',
  'coa',
  'journals',
  'sales',
  'purchases',
  'inventory',
  'banking',
  'taxes',
  'reports',
  'import',
  'fixedAssets',
  'payroll',
  'settings'
] as const;

export type ModuleId = (typeof MODULE_IDS)[number];

/** Shown in its own permission group in Settings → Users. */
export const SIDEBAR_MODULE_IDS = MODULE_IDS.filter((id) => id !== 'cashbook');

export const SETTINGS_SECTION_IDS = [
  'organization',
  'branding',
  'language',
  'regional',
  'users',
  'costCenters',
  'security',
  'eInvoicing',
  'reportSchedule',
  'budget',
  'taxGl',
  'fiscalPeriod',
  'auditLog'
] as const;

export type SettingsSectionId = (typeof SETTINGS_SECTION_IDS)[number];

export type MemberPermissionOverrides = {
  modules?: Partial<Record<ModuleId, boolean>>;
  settings?: Partial<Record<SettingsSectionId, boolean>>;
  cashbook?: CashbookPermissionOverrides;
};

export type MemberAccess = {
  modules: Record<ModuleId, boolean>;
  settings: Record<SettingsSectionId, boolean>;
  cashbook: CashbookAccess;
};

const MANAGER_MODULES: ModuleId[] = [
  'dashboard',
  'cashbook',
  'coa',
  'journals',
  'sales',
  'purchases',
  'inventory',
  'banking',
  'taxes',
  'reports',
  'import',
  'fixedAssets',
  'payroll',
  'settings'
];

const BOOKKEEPER_MODULES: ModuleId[] = ['dashboard', 'cashbook', 'reports', 'settings'];

const VIEWER_MODULES: ModuleId[] = ['dashboard', 'cashbook', 'reports', 'settings'];

const MANAGER_SETTINGS: SettingsSectionId[] = [
  'organization',
  'branding',
  'language',
  'regional',
  'costCenters',
  'security',
  'eInvoicing',
  'reportSchedule',
  'budget',
  'taxGl',
  'fiscalPeriod',
  'auditLog'
];

const BOOKKEEPER_SETTINGS: SettingsSectionId[] = ['language', 'regional', 'security'];

const VIEWER_SETTINGS: SettingsSectionId[] = ['language'];

function allModules(enabled: boolean): Record<ModuleId, boolean> {
  return Object.fromEntries(MODULE_IDS.map((id) => [id, enabled])) as Record<ModuleId, boolean>;
}

function allSettings(enabled: boolean): Record<SettingsSectionId, boolean> {
  return Object.fromEntries(SETTINGS_SECTION_IDS.map((id) => [id, enabled])) as Record<
    SettingsSectionId,
    boolean
  >;
}

function modulesFor(ids: ModuleId[]): Record<ModuleId, boolean> {
  const set = new Set(ids);
  return Object.fromEntries(MODULE_IDS.map((id) => [id, set.has(id)])) as Record<ModuleId, boolean>;
}

function settingsFor(ids: SettingsSectionId[]): Record<SettingsSectionId, boolean> {
  const set = new Set(ids);
  return Object.fromEntries(SETTINGS_SECTION_IDS.map((id) => [id, set.has(id)])) as Record<
    SettingsSectionId,
    boolean
  >;
}

export function defaultMemberAccessForRole(role: OrgRole): MemberAccess {
  if (role === 'owner') {
    return {
      modules: allModules(true),
      settings: allSettings(true),
      cashbook: resolveCashbookAccess('owner')
    };
  }
  if (role === 'accountant') {
    return {
      modules: modulesFor(MANAGER_MODULES),
      settings: settingsFor(MANAGER_SETTINGS),
      cashbook: resolveCashbookAccess('accountant')
    };
  }
  if (role === 'bookkeeper') {
    return {
      modules: modulesFor(BOOKKEEPER_MODULES),
      settings: settingsFor(BOOKKEEPER_SETTINGS),
      cashbook: resolveCashbookAccess('bookkeeper')
    };
  }
  return {
    modules: modulesFor(VIEWER_MODULES),
    settings: settingsFor(VIEWER_SETTINGS),
    cashbook: resolveCashbookAccess('viewer')
  };
}

export function canAccessModule(access: MemberAccess, moduleId: ModuleId): boolean {
  return access.modules[moduleId];
}

export function canAccessSettingsSection(
  access: MemberAccess,
  sectionId: SettingsSectionId
): boolean {
  return access.settings[sectionId];
}

export function resolveMemberAccess(
  role: OrgRole,
  overrides?: MemberPermissionOverrides | null,
  legacyCashbook?: CashbookPermissionOverrides | null
): MemberAccess {
  if (role === 'owner') return defaultMemberAccessForRole('owner');

  const defaults = defaultMemberAccessForRole(role);
  if (!overrides && !legacyCashbook) return defaults;

  const cashbookOverrides = overrides?.cashbook ?? legacyCashbook ?? undefined;

  return {
    modules: { ...defaults.modules, ...overrides?.modules },
    settings: { ...defaults.settings, ...overrides?.settings },
    cashbook: resolveCashbookAccess(role, cashbookOverrides)
  };
}

export function hasMemberPermissionOverrides(
  overrides?: MemberPermissionOverrides | null,
  legacyCashbook?: CashbookPermissionOverrides | null
): boolean {
  if (hasCashbookPermissionOverrides(legacyCashbook)) return true;
  if (!overrides) return false;
  if (overrides.modules && Object.keys(overrides.modules).length > 0) return true;
  if (overrides.settings && Object.keys(overrides.settings).length > 0) return true;
  if (hasCashbookPermissionOverrides(overrides.cashbook)) return true;
  return false;
}

/** Role-based helpers still used where per-user overrides are not loaded. */
export function roleCanPostDocuments(role: OrgRole): boolean {
  return canPostDocuments(role);
}

export function roleCanManageTeam(role: OrgRole): boolean {
  return canManageTeam(role);
}

export function roleCanManageUsers(role: OrgRole): boolean {
  return canManageUsers(role);
}

function parseBooleanMap<T extends string>(
  raw: unknown,
  allowed: readonly T[]
): Partial<Record<T, boolean>> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const out: Partial<Record<T, boolean>> = {};
  for (const key of allowed) {
    if (typeof data[key] === 'boolean') out[key] = data[key] as boolean;
  }
  return Object.keys(out).length ? out : undefined;
}

export function parseMemberPermissionOverrides(raw: unknown): MemberPermissionOverrides | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const modules = parseBooleanMap(data.modules, MODULE_IDS);
  const settings = parseBooleanMap(data.settings, SETTINGS_SECTION_IDS);
  const cashbook = parseCashbookPermissionOverrides(data.cashbook);
  if (!modules && !settings && !cashbook) return undefined;
  return {
    ...(modules ? { modules } : {}),
    ...(settings ? { settings } : {}),
    ...(cashbook ? { cashbook } : {})
  };
}

export function memberPermissionsFromMemberDoc(data: Record<string, unknown> | undefined): {
  overrides?: MemberPermissionOverrides;
  legacyCashbook?: CashbookPermissionOverrides;
} {
  const overrides = parseMemberPermissionOverrides(data?.permissions);
  const legacyCashbook = parseCashbookPermissionOverrides(data?.cashbookPermissions);
  return { overrides, legacyCashbook };
}
